import { useQuery } from '@tanstack/react-query';
import { authClient, ApiError, unwrap } from '@/shared/api-client';

export interface SystemStatus {
  bootstrapped: boolean;
}

export interface BootstrapInput {
  token: string;
  email: string;
  password: string;
}

interface SetupErrorBody {
  error?: string;
  field?: string | null;
  message?: string;
}

/**
 * Thrown when bootstrap is rejected. Extends {@link ApiError} for `status`/`code` while
 * keeping its extra `field`, which SetupWizard maps onto the offending form input.
 */
export class SetupError extends ApiError {
  readonly field: string | null;
  constructor(status: number, field: string | null, message: string) {
    super(status, null, message);
    this.field = field;
    this.name = 'SetupError';
  }
}

export async function bootstrap(input: BootstrapInput): Promise<void> {
  // unwrap collapses errors to (status, code); setup needs the richer `field`, so read the
  // raw apiClient result and build the SetupError ourselves.
  const { error, response } = await authClient.POST('/api/v1/auth/setup', { body: input });
  if (response.ok) return;
  const body = error as SetupErrorBody | undefined;
  throw new SetupError(
    response.status,
    body?.field ?? null,
    body?.message ?? `Setup failed (${response.status})`,
  );
}

export const systemStatusQueryKey = ['system', 'status'] as const;

async function fetchSystemStatus(): Promise<SystemStatus> {
  return (await unwrap(authClient.GET('/api/v1/auth/system/status'))) as SystemStatus;
}

export function useSystemStatus() {
  return useQuery({
    queryKey: systemStatusQueryKey,
    queryFn: fetchSystemStatus,
    staleTime: Infinity,
    retry: false,
  });
}
